/**
 * @fileoverview Limit Controller - 制限表示管理（抽象化版）
 */

import { getActiveLimitDriver } from '../../gameplay/game-controls.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';

import { onPathChange } from './subscription-utils.js';

let displayUpdateRafId = 0;
let hasLoggedMissingElements = false;

/**
 * DOM要素取得（存在チェック＋軽ログ）
 */
function getDOMElements() {
  const labelEl = document.getElementById('ct-seconds');
  const progressEl = document.getElementById('ct-progress');

  if ((!labelEl || !progressEl) && !hasLoggedMissingElements) {
    MinimalErrorTracker.info('LIMIT_UI', 'Timer DOM elements not found');
    hasLoggedMissingElements = true;
  }

  if (labelEl && progressEl) {
    hasLoggedMissingElements = false;
  }

  return { labelEl, progressEl };
}

/**
 * 制限表示の更新（状態変更時のDOM更新）
 */
export function updateLimitDisplay(gameState) {
  const elements = getDOMElements();
  if (!elements.labelEl && !elements.progressEl) {
    return;
  }

  const driver = getActiveLimitDriver();
  applyLimitDriverUpdate(gameState, driver, elements);
}

function applyLimitDriverUpdate(gameState, driver, elements) {
  const { labelEl, progressEl } = elements;
  if (!labelEl && !progressEl) {
    return;
  }

  if (!driver) {
    return;
  }

  if (isIntroPhaseActive(driver, gameState)) {
    updateIntroAnimation(driver, gameState, labelEl, progressEl);
    return;
  }

  updateCountdownDisplay(driver, gameState, labelEl, progressEl);
}

function isIntroPhaseActive(driver, gameState) {
  if (!driver || typeof driver.isIntroAnimating !== 'function') {
    return false;
  }

  try {
    if (!driver.isIntroAnimating(gameState)) {
      return false;
    }
  } catch (_error) {
    return false;
  }

  return gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE) === 'intro';
}

/**
 * �C���g���A�j���[�V��������X�V�i�h���C�o�o�R�̂݁A�p�X���ǂ݁j
 */
function updateIntroAnimation(driver, gameState, labelEl, progressEl) {
  const progress = resolveIntroProgress(driver, gameState);
  const displayLabel = resolveIntroDisplayLabel(driver, gameState);
  const maxValue = resolveLimitMaxValue(gameState, displayLabel);

  if (labelEl) {
    const currentValue = Math.ceil(maxValue * progress);
    labelEl.textContent = String(currentValue);
  }

  if (progressEl) {
    const circumference = getOrInitializeCircumference(progressEl);
    setRingPercent(progressEl, circumference, progress * 100);
  }
}

/**
 * �ʏ����X�V�i�h���C�o���ۂ̂݁A���[�h����Ȃ��j
 */
function updateCountdownDisplay(driver, gameState, labelEl, progressEl) {
  const remainingValue = resolveRemainingValue(driver, gameState);

  if (labelEl) {
    labelEl.textContent = String(remainingValue);
  }

  if (progressEl) {
    const circumference = getOrInitializeCircumference(progressEl);
    const progressPercent = resolveProgressRatio(driver, gameState) * 100;
    setRingPercent(progressEl, circumference, progressPercent);
  }
}

function resolveIntroProgress(driver, gameState) {
  const driverProgress = toFiniteNumber(callDriverMethod(driver, 'getIntroProgress', gameState));
  if (driverProgress !== null) {
    return clamp01(driverProgress);
  }

  const stateProgress = toFiniteNumber(
    gameState.getState(STATE_PATHS.LIMIT_RUNTIME_INTRO_PROGRESS),
  );
  if (stateProgress !== null) {
    return clamp01(stateProgress);
  }

  return 1;
}

function resolveIntroDisplayLabel(driver, gameState) {
  const label = callDriverMethod(driver, 'getDisplayLabel', gameState);
  return label === 'moves' ? 'moves' : 'seconds';
}

function resolveLimitMaxValue(gameState, displayLabel) {
  if (displayLabel === 'moves') {
    const maxMoves = toFiniteNumber(gameState.getState(STATE_PATHS.LIMIT_CONFIG_MAX_MOVES));
    return maxMoves !== null ? Math.max(0, Math.round(maxMoves)) : 0;
  }

  const timeLimitMs = toFiniteNumber(gameState.getState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS));
  if (timeLimitMs === null) {
    return 0;
  }

  return Math.max(0, Math.ceil(timeLimitMs / 1000));
}

function resolveRemainingValue(driver, gameState) {
  const fromDriver = toFiniteNumber(callDriverMethod(driver, 'getRemainingValue', gameState));
  if (fromDriver !== null) {
    return fromDriver < 0 ? 0 : fromDriver;
  }

  const stateValue = toFiniteNumber(gameState.getState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE));
  if (stateValue !== null) {
    return stateValue < 0 ? 0 : stateValue;
  }

  return 0;
}

function resolveProgressRatio(driver, gameState) {
  const ratio = toFiniteNumber(callDriverMethod(driver, 'getProgressRatio', gameState));
  return ratio !== null ? clamp01(ratio) : 0;
}

function callDriverMethod(driver, methodName, gameState) {
  if (!driver || typeof driver[methodName] !== 'function') {
    return undefined;
  }

  try {
    return driver[methodName](gameState);
  } catch (_error) {
    return undefined;
  }
}

function toFiniteNumber(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function clamp01(value) {
  if (value <= 0) {
    return 0;
  }
  if (value >= 1) {
    return 1;
  }
  return value;
}

export function resetLimitDisplay(_gameState) {
  const { labelEl, progressEl } = getDOMElements();

  if (labelEl) {
    labelEl.textContent = '0';
  }

  if (progressEl) {
    const circumference = getOrInitializeCircumference(progressEl);
    setRingPercent(progressEl, circumference, 0);
  }
}

/**
 * 即座に空表示
 */
export function instantEmptyDisplay() {
  const { labelEl } = getDOMElements();
  if (labelEl) {
    labelEl.textContent = '0';
  }
}

// ========================================
// リング描画関数
// ========================================
function getOrInitializeCircumference(progressEl) {
  if (!progressEl) return 0;

  if (progressEl._circumference) {
    return progressEl._circumference;
  }

  const r = Number(progressEl.getAttribute('r')) || 36;
  const circumference = 2 * Math.PI * r;

  progressEl._circumference = circumference;
  progressEl.style.strokeDasharray = `${circumference} ${circumference}`;
  progressEl.style.strokeDashoffset = String(circumference);

  return circumference;
}

function setRingPercent(progressEl, circumference, percent) {
  if (!progressEl || circumference === 0) return;

  const p = Math.max(0, Math.min(100, percent));
  const offset = circumference - (p / 100) * circumference;
  progressEl.style.strokeDashoffset = String(offset);
}

// ========================================
// 更新ループ管理（RAF統合：常時起動）
// ========================================
function startDisplayUpdateLoop(gameState) {
  if (typeof requestAnimationFrame !== 'function') {
    return;
  }

  if (displayUpdateRafId !== 0 && typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(displayUpdateRafId);
    displayUpdateRafId = 0;
  }

  const updateLoop = (nowMs) => {
    const elements = getDOMElements();

    if (!elements.labelEl && !elements.progressEl) {
      displayUpdateRafId = 0;
      return;
    }

    const driver = getActiveLimitDriver();
    if (!driver) {
      displayUpdateRafId = requestAnimationFrame(updateLoop);
      return;
    }

    if (typeof driver.update === 'function') {
      try {
        driver.update(gameState, nowMs);
      } catch (_error) {
        // Swallow update errors to keep the loop alive during development
      }
    }

    applyLimitDriverUpdate(gameState, driver, elements);
    displayUpdateRafId = requestAnimationFrame(updateLoop);
  };

  displayUpdateRafId = requestAnimationFrame(updateLoop);
}

// �����̊g���p�Ɏc���Ă���
// function stopDisplayUpdateLoop() {
//   if (displayUpdateRafId !== 0) {
//     cancelAnimationFrame(displayUpdateRafId);
//     displayUpdateRafId = 0;
//   }
// }

/**
 * �R���g���[���[������
 */
export function initializeLimitController(gameState) {
  // RAF ループを常時起動
  startDisplayUpdateLoop(gameState);

  // 状態変更時のDOM更新監視
  return onPathChange(
    gameState,
    'limit',
    () => {
      updateLimitDisplay(gameState);
    },
    true,
  );
}
